import {populateData, setDefaultCollapsedState, setVisitsAreLoading} from "./BookingFormActions";
import {bookingFormActionTypes} from "./BookingFormActionTypes";
import {DateTime} from "luxon";
import {globalErrorCondition} from "../../../Helpers/ErrorHelper";
import { VISITORS_API_URL } from "../Visitors";

export const initialState = {
    isLoading: true,
    isSaving: false,
    isImportModalOpen: false,
    isConfirmationModalOpen: false,
    willPrintPassAfterSubmit: false,

    // I'm not sure these two are ever used
    editVisitId: '',
    editBookingId: '',

    bookingType: '', // ['group', 'single', 'walk-in']
    editEventRecurrenceType: 'standalone', // ['standalone', 'single', 'series']

    booking: {},
    visits: [{}],
    hosts: [{}],
    visitors: [{}],

    errors: {},
};

export const BookingFormReducer = (state = initialState, action) => {
    switch (action.type) {
        case bookingFormActionTypes.INITIALISE_FORM: {

            const prototypes = (action.payload.prototypes)
                ? {
                    booking: {...action.payload.prototypes.booking},
                    visits: [{...action.payload.prototypes.visit}],
                    visitors: [{...action.payload.prototypes.visitor}],
                    hosts: [{...action.payload.prototypes.host}],
                }
                : {
                    booking: {},
                    visits: [{}],
                    visitors: [{}],
                    hosts: [{}],
                }
            ;

            return {
                ...state,

                isLoading: true,
                isSaving: false,
                isImportModalOpen: false,
                isConfirmationModalOpen: false,
                willPrintPassAfterSubmit: false,

                ...prototypes,

                errors: {}
            }
        }

        case bookingFormActionTypes.RESET_FORM: {
            const prototypes = action.payload.prototypes;
            const previousHosts = action.payload.previousHosts ?? null;

            const newState = {
                ...state,
                ...initialState,
                isLoading: false, // Skip loading process as we've already initialised everything
                bookingType: state.bookingType, // Persist previous bookingType
                booking: {...prototypes.booking}, // Reset all the main entities to the default prototypes
                visits: [{...prototypes.visit}],
                visitors: [{...prototypes.visitor}],
                hosts: (previousHosts)
                    ? [...previousHosts]
                    : {...prototypes.host}
            };

            // Ensure form is reset with 2 visits/visitors for group bookings
            if (state.bookingType === 'group') {
                newState.visits = [{...prototypes.visit}, {...prototypes.visit}]
                newState.visitors = [{...prototypes.visitor}, {...prototypes.visitor}]
            }

            return newState;
        }

        case bookingFormActionTypes.SET_BOOKING_TYPE: {
            return {
                ...state,
                bookingType: action.value,
                errors: {}
            }
        }

        case bookingFormActionTypes.SET_RECURRENCE_EDIT_TYPE: {
            return {...state, editEventRecurrenceType: action.value}
        }

        case bookingFormActionTypes.SET_IS_LOADING: {
            return {...state, isLoading: action.value}
        }

        case bookingFormActionTypes.SET_IS_SAVING: {
            return {...state, isSaving: action.value}
        }

        case bookingFormActionTypes.SET_IMPORT_MODAL_OPEN: {
            return {...state, isImportModalOpen: action.value}
        }

        case bookingFormActionTypes.SET_CONFIRMATION_MODAL_OPEN: {
            return {...state, isConfirmationModalOpen: action.value}
        }

        case bookingFormActionTypes.SET_WILL_PRINT_PASS_AFTER_SUBMIT: {
            return {...state, willPrintPassAfterSubmit: action.value}
        }

        case bookingFormActionTypes.POPULATE_DATA: {
            if (action.payload.data.resource) {
                return {
                    ...state,
                    bookingType: action.payload.data.resource.visits[0].type,
                    booking: {...action.payload.data.resource.booking},
                    visits: [...action.payload.data.resource.visits],
                    visitors: [...action.payload.data.resource.visitors],
                    hosts: [...action.payload.data.resource.hosts]
                };
            } else {
                const prototypes = action.payload.prototypes;
                return {
                    ...state,
                    booking: {...prototypes.booking},
                    visits: [{...prototypes.visit}],
                    visitors: [{...prototypes.visitor}],
                    hosts: [{...prototypes.host}]
                };
            }
        }

        case bookingFormActionTypes.SET_ERRORS: {
            return {
                ...state,
                errors: {...action.value}
            }
        }

        /**
         * Set the initial 'collapsed' state, so that hosts, visits, visitors
         * will start collapsed if there's more than one
         */
        case bookingFormActionTypes.SET_DEFAULT_COLLAPSED_STATE: {
            let newState = {...state};

            // Make a hierarchical look up table to quickly check if any entity has an error
            let errors = {hosts: [], visits: [], visitors: []};
            for (let key in state.errors) {
                let [type, index, field] = key.split('.');
                errors[type][index] = true;
            }

            if (newState.hosts.length > 1) {
                let hostErrors = errors.hosts;
                newState.hosts = newState.hosts.map((item, index) => {
                    let errorExists = hostErrors[index];
                    return { ...item, _isCollapsed: !errorExists}
                })
            }

            if (newState.visits.length > 1) {
                let visitErrors = errors.visits;
                newState.visits = newState.visits.map((item, index) => {
                    let errorExists = visitErrors[index];
                    return { ...item, _isCollapsed: !errorExists}
                })
            }

            if (newState.visitors.length > 1) {
                let visitorErrors = errors.visitors;
                newState.visitors = newState.visitors.map((item, index) => {
                    let errorExists = visitorErrors[index];
                    return { ...item, _isCollapsed: !errorExists}
                })
            }
            return newState;
        }

        case bookingFormActionTypes.UPDATE_HOST: {
            let {name, value, index} = action.payload;

            let updatedErrors = {...state.errors};
            delete updatedErrors[`hosts.${index}.${name}`];

            return {
                ...state,
                hosts: state.hosts.map((item, hostIndex) => {
                    return (hostIndex !== index)
                        ? item
                        : { ...item, [name]: value }
                }),
                errors: { ...updatedErrors }
            };
        }

        case bookingFormActionTypes.UPDATE_VISIT: {
            let {name, value, index} = action.payload;
            let updatedErrors = removeErrors(state.errors, `visits.${index}.${name}`);
            let newErrors = validateVisit(name, value, `visits.${index}.`);

            return {
                ...state,
                visits: state.visits.map((item, visitIndex) => {
                    return (visitIndex !== index)
                        ? item
                        : { ...item, [name]: value}
                }),
                errors: {
                    ...updatedErrors,
                    ...newErrors
                }
            };
        }

        case bookingFormActionTypes.UPDATE_ALL_VISITS: {
            let {name, value} = action.payload;

            let updatedErrors = removeErrors(state.errors, `visits.0.${name}`);
            let newErrors = validateVisit(name, value, `visits.0.`);

            return {
                ...state,
                visits: state.visits.map((item) => {
                    return { ...item, [name]: value}
                }),
                errors: {
                    ...updatedErrors,
                    ...newErrors
                }
            };
        }

        case bookingFormActionTypes.UPDATE_ALL_VISITS_EXCEPT_LAST: {
            let {name, value} = action.payload;

            let updatedErrors = removeErrors(state.errors, `visits.0.${name}`);
            let newErrors = validateVisit(name, value, `visits.0.`);

            return {
                ...state,
                visits: state.visits.map((item, index) => {
                    return (index === (state.visits.length-1))
                        ? item
                        : { ...item, [name]: value};
                }),
                errors: {
                    ...updatedErrors,
                    ...newErrors
                }
            };
        }

        case bookingFormActionTypes.UPDATE_VISITOR: {
            let {name, value, index} = action.payload;
            let updatedErrors = removeErrors(state.errors, `visitors.${index}.${name}`)

            return {
                ...state,
                visitors: state.visitors.map((item, visitorIndex) => {
                    return (visitorIndex !== index)
                        ? item
                        : { ...item, [name]: value }
                }),
                errors: { ...updatedErrors }
            };
        }

        case bookingFormActionTypes.ADD_HOST: {
            // Collapse only previous host
            let newHosts = [...state.hosts]
            if (newHosts.length > 0) {
                newHosts[newHosts.length - 1]._isCollapsed = true;
            }

            let newHost = {...action.payload.prototypes.host, ...action.payload.host};

            return {
                ...state,
                hosts: [...newHosts, newHost]
            }
        }

        case bookingFormActionTypes.ADD_VISIT_AND_VISITOR: {
            // Collapse only previous host
            let newVisits = [...state.visits]
            let newVisitors = [...state.visitors]

            if (!action.payload.keepExpanded) {
                if (newVisits.length > 0) {
                    newVisits[newVisits.length - 1]._isCollapsed = true;
                }

                if (newVisitors.length > 0) {
                    newVisitors[newVisitors.length - 1]._isCollapsed = true;
                }
            }

            let newVisit = {...action.payload.prototypes.visit, ...action.payload.visit};
            let newVisitor = {...action.payload.prototypes.visitor, ...action.payload.visitor};

            // Prefill due_at date from previous visitor
            if (!newVisit.due_at && state.visits.length) {
                newVisit.due_at = state.visits[state.visits.length - 1].due_at;
            }

            return {
                ...state,
                visits: [...newVisits, newVisit],
                visitors: [...newVisitors, newVisitor]
            }
        }

        case bookingFormActionTypes.ADD_VISIT: {
            let visit = {...action.payload.prototypes.visit, ...action.payload.visit};
            return {...state, visits: [...state.visits, visit] }
        }

        case bookingFormActionTypes.ADD_VISITOR: {
            let newVisitors = state.visitors.map((item, index) => {
                return {...item, _isCollapsed: true}
            });

            let newVisitor = {...action.payload.prototypes.visitor, ...action.payload.visitor};
            return {
                ...state,
                visitors: [...newVisitors, newVisitor]
            }
        }

        case bookingFormActionTypes.DELETE_HOST: {
            let newHosts = [
                ...state.hosts.slice(0, action.payload.index),
                ...state.hosts.slice(action.payload.index + 1, state.hosts.length)
            ];

            if (newHosts.length < 1) {
                newHosts = [...newHosts, {...action.payload.prototypes.host}];
            }

            return {...state, hosts: newHosts};
        }

        case bookingFormActionTypes.DELETE_VISIT_AND_VISITOR: {
            let newVisits = [
                ...state.visits.slice(0, action.payload.index),
                ...state.visits.slice(action.payload.index + 1, state.visits.length)
            ];
            let newVisitors = [
                ...state.visitors.slice(0, action.payload.index),
                ...state.visitors.slice(action.payload.index + 1, state.visitors.length)
            ]

            if (newVisits.length < 1) {
                newVisits = [...newVisits, {...action.payload.prototypes.visit}];
                newVisitors = [...newVisitors, {...action.payload.prototypes.visitor}];
            }

            return {...state, visits: newVisits, visitors: newVisitors};
        }

        case bookingFormActionTypes.DELETE_VISIT: {
            return {
                ...state,
                visits: [
                    ...state.visits.slice(0, action.index),
                    ...state.visits.slice(action.index + 1, state.visits.length)
                ]
            }
        }

        case bookingFormActionTypes.DELETE_ALL_VISITS_EXCEPT_FIRST: {
            return {
                ...state,
                visits: [...state.visits.slice(0,1)]
            }
        }

        case bookingFormActionTypes.DELETE_EMPTY_VISITS_AND_VISITORS: {
            let lastNonEmptyVisit = findLastNonEmptyIndex(state.visits, ['due_at'], action.payload.keepFirstEmpty);
            let lastNonEmptyVisitors = findLastNonEmptyIndex(state.visitors, ['name', 'company', 'email'], action.payload.keepFirstEmpty);
            let lastIndex = (action.payload.useLowestIndex)
                ? Math.min(lastNonEmptyVisit, lastNonEmptyVisitors)
                : Math.max(lastNonEmptyVisit, lastNonEmptyVisitors)
            ;

            if (action.payload.emptySlotAtEnd) {
                lastIndex++;
            }

            if (lastIndex < 0) {
                return {...state, visits: [], visitors: []}
            } else {
                return {
                    ...state,
                    visits: [...state.visits.slice(0, lastIndex+1)],
                    visitors: [...state.visitors.slice(0, lastIndex+1)]
                }
            }
        }

        case bookingFormActionTypes.DELETE_VISITOR: {
            return {
                ...state,
                visitors: [
                    ...state.visitors.slice(0, action.index),
                    ...state.visitors.slice(action.index + 1)
                ]
            }
        }

        default:
            return state;
    }
}

/**
 * Finds the last index in the array which contains a non-empty entry in any one of the
 * array fields specified in checkFields
 * @param array
 * @param checkFields
 * @param keepFirstEmpty  If true, will allow a single empty visit/visitor at the beginning of the list
 * @returns {number}
 */
const findLastNonEmptyIndex = (array, checkFields, keepFirstEmpty) => {
    let index = array.length - 1;
    let checkIndex = (keepFirstEmpty) ? 1 : 0;
    while (index >= checkIndex) {
        let checkItem = array[index]
        let empty = true;
        for (var i in checkFields) {
            if (!checkFields.hasOwnProperty(i)) { continue; }
            let checkField = checkFields[i];
            if (checkItem[checkField] && checkItem[checkField].length > 0) {
                empty = false;
            }
        }
        if (!empty) {
            return index;
        }
        index--;
    }
    return index
}

/**
 *
 * @param editVisitId
 * @param editBookingId
 * @returns {function(*): Promise<void>}
 */
export const getCreateFormData = (editVisitId, editBookingId) => async dispatch => {
    try {
        const {data} = await axios.get(`${VISITORS_API_URL}bookings/create`);
        dispatch(populateData(data));
    } catch (error) {
        globalErrorCondition(error);
    } finally {
        dispatch(setVisitsAreLoading(false));
    }
}

export const getEditFormData = (editVisitId, editingVisitDate) => async dispatch => {
    dispatch(setVisitsAreLoading(true));

    try {
        if (editVisitId) {
            var url = `${VISITORS_API_URL}visits/${editVisitId}/edit`;
        } else if (editBookingId) {
            url = `${VISITORS_API_URL}bookings/${editBookingId}/edit}`
        }
        const {data} = await axios.get(url, {
            params: {
                eventDate: editingVisitDate ?? null
            },
        });
        dispatch(populateData(data));
        dispatch(setDefaultCollapsedState());
    } catch (error) {
        globalErrorCondition(error);
    } finally {
        dispatch(setVisitsAreLoading(false));
    }
}

/**
 * Clear any errors that may previously have been found for this field,
 * and any subfields
 * @param errors
 * @param fieldName
 * @returns {*}
 */
const removeErrors = (errors, fieldName) => {
    let updatedErrors = {...errors};
    delete updatedErrors[fieldName];

    let childKeys = Object.keys(updatedErrors);

    for (var i in childKeys) {
        if (childKeys[i].startsWith(fieldName)) {
            delete updatedErrors[childKeys[i]];
        }
    }

    return updatedErrors;
}

const validateVisit = (fieldName, value, errorPrefix) => {
    let errors = {};

    switch (fieldName) {
        case 'due_at':
            const date = DateTime.fromISO(value).startOf('day');
            const today = DateTime.local().startOf('day');
            if (date < today) {
                errors[errorPrefix + 'due_at'] = 'Please enter a valid date that is not in the past';
            }
            break;

        default:
            break;
    }

    return errors;
}
